<template>
  <v-card-text v-if="loading"></v-card-text>
  <v-card-text v-else>
    <v-card-actions class="criterio-header">
      <span class="custom-font-light custom-blue-text custom-title">{{ criterio.nombre }}</span>
      <v-spacer></v-spacer>
      <span class="custom-font-regular text-grey mr-1">
        Artículo:
        <a @click="$refs.instructivo.openDialog()">{{ criterio.articulo }}</a> del {{criterio.reglamento}}
        | Categoría: {{ criterio.categoria }}
      </span>
    </v-card-actions>
    <v-layout row wrap class="criterio-subheader" v-if="showDefinicionesSAF">
      <v-flex xs3 pa-0 ma-0>
        <div class="custom-font-semi-bold text-grey mt-4">Definiciones LPF:</div>
      </v-flex>
      <v-flex v-if="expire" xs5 pa-0 ma-0>
        <ExpirationDate :initial-date="criterio.expiration.date" :criterio="criterio"
                        :without-term="criterio.expiration.withoutTerm"
                        @updateCriterionExpiration="updateCriterionExpiration"
                        class="pa-0 justify-center">
          <span class="custom-font-regular text-grey mr-2">Vencimiento:</span>
        </ExpirationDate>
      </v-flex>
      <v-flex v-if="isPerson" xs4 pa-0 ma-0>
        <v-checkbox color="primary lighten-2" v-model="criterio.createsUsers" :class="['mt-4 pa-0', { 'justify-end': expire }]"
                    @change="setCreateUsers(criterio)" light label="Crear usuario"></v-checkbox>
      </v-flex>
    </v-layout>
    <v-card-actions class="table-actions">
      <v-text-field v-model="search" solo light append-icon="search" label="Buscar por nombre de requerimiento..."
                    single-line hide-details flat class="search-bar custom-font-regular"></v-text-field>
      <v-spacer></v-spacer>
      <template v-if="estadosRequerimientos.length">
        <span class="select-label custom-font-light text-grey custom-text">Ver solamente los requerimientos de estado</span>
        <v-select class="select-element custom-font-regular" v-model="selected" :items="estados" light solo label="Estado"
                  hide-details no-data-text="No hay datos disponibles" outline dense flat></v-select>
      </template>
    </v-card-actions>
    <v-divider></v-divider>

    <div v-if="allAllowedAdmin" style="display: flex; justify-content: end; align-items: center; width: 100%; padding: 0; margin: 0;">
      <v-btn
        v-if="hasIncompleteRequerimientos"
        style="height: 30px; margin-bottom: -5px;"
        elevation="2"
        color="primary"
        class="white--text"
        :loading="loadingReiterar"
        :disabled="loadingReiterar"
        @click="handleReiterarObservaciones()">
        Reiterar observaciones
      </v-btn>
      <v-btn
        style="height: 30px; margin-bottom: -5px;"
        elevation="2"
        color="#1787c9" class="submit-button white--text"
        @click="changeRequerimientoStatus()">
        Aprobar todos
      </v-btn>
    </div>

    <v-data-table :headers="headers" :items="requerimientos" hide-actions item-key="pk" no-data-text="No hay datos disponibles"
                  no-results-text="No se encontraron registros que coincidan" light :search="search" ref="table" disable-initial-sort
                  class="elevation-1 custom-font-regular blue-table-header white-text-table-header dense-table-header mt-3">
      <template slot="items" slot-scope="props">
        <tr @click="openRequerimiento(props)" :class="[{ 'selected-row' : props.expanded }, 'mouse-pointer']">
          <td class="text-xs-left">{{ props.item.nombre }}</td>
          <td class="text-xs-right">
            <v-tooltip bottom>
              <v-chip color="#dc1975" label small v-if="props.item.confidential"
                      slot="activator" text-color="white">CONFIDENCIAL</v-chip>
              <span>Este requerimiento es confidencial, sólo podrá ser visto con los permisos correspondientes</span>
            </v-tooltip>
            <v-tooltip bottom>
              <v-chip color="yellow" label small v-if="props.item.optional"
                      slot="activator">OPCIONAL</v-chip>
              <span>Este requerimiento es opcional</span>
            </v-tooltip>
            <v-tooltip bottom>
              <v-chip color="yellow" label small v-if="!props.item.isClubUserAllowedToCorrect && !$store.getters.isSafRole"
                      slot="activator">Pendiente de ser revisado por LPF</v-chip>
              <span>Pendiente de ser revisado por LPF</span>
            </v-tooltip>
            <template v-if="isAdminRole(props.item.allowed) && props.expanded">
              <StatusIcon :status="currentStatus"></StatusIcon>
              <span class="requerimiento-status">{{ currentStatus }}</span>
              <span @click.stop="">
              <RequerimientoEstado ref="estado" :current-status="currentStatus" :requerimiento="props.item"
                                   :estados="estadosRequerimientos" @changeCurrentStatus="changeCurrentStatus"></RequerimientoEstado>
              </span>
            </template>
            <template v-else>
              <StatusIcon :status="props.item.estadoDisplay"></StatusIcon>
              <span class="requerimiento-status">{{ props.item.estadoDisplay }}</span>
              <NullEstado ref="estado"></NullEstado>
            </template>
          </td>
        </tr>
      </template>
      <template slot="expand" slot-scope="{item}">
        <RequerimientoDetail @updateRequerimiento="updateAfterRequest" :requerimiento="item" :is-ficha="isFicha" :draft="draft"
                             :impacts-repo="criterio.impactsRepo" :current-status="currentStatus" :ref="item.pk"></RequerimientoDetail>
      </template>
    </v-data-table>
    <ConfirmationDialog :function-yes="bindingFunction" ref="confirm"
                        text="Se perderán los cambios del requerimiento actual, ¿desea continuar?"></ConfirmationDialog>
    <v-snackbar v-model="savingAlert" auto-height color="error">
      <span>El requerimiento está siendo guardado. Por favor espere.</span>
      <v-btn icon @click="savingAlert = false"><v-icon>close</v-icon></v-btn>
    </v-snackbar>
    <InstructivoCriterioDialog :criterio="criterio" ref="instructivo"></InstructivoCriterioDialog>
  </v-card-text>
</template>

<script>
import StatusIcon from './StatusIcon'
import NullEstado from './RequerimientoDetail/NullEstado'
import ExpirationDate from './ExpirationDate'
import InstructivoCriterioDialog from './InstructivoCriterioDialog'
import RequerimientoEstado from './RequerimientoDetail/RequerimientoEstado'
import RequerimientoDetail from './RequerimientoDetail/RequerimientoDetail'
import ConfirmationDialog from '../../../common/ui/ConfirmationDialog'

export default {
  components: {
    StatusIcon,
    NullEstado,
    ExpirationDate,
    RequerimientoEstado,
    RequerimientoDetail,
    ConfirmationDialog,
    InstructivoCriterioDialog
  },

  props: {
    criterio: Object,
    estadosRequerimientos: {
      type: Array,
      default: () => []
    },
    draft: Boolean,
    isFicha: Boolean
  },

  data () {
    return {
      headers: [
        { text: 'Nombre', value: 'nombre', sortable: false },
        { text: 'Estado', value: 'estadoDisplay', align: 'right', sortable: false }
      ],
      selected: 'Todos',
      search: '',
      estados: [],
      currentStatus: '',
      bindingFunction: null,
      savingAlert: false,
      flag: false,
      loadingReiterar: false
    }
  },

  computed: {
    loading () {
      return !this.criterio.hasOwnProperty('criterionDefinition')
    },
    requerimientos () {
      let requerimientos = this.criterio.requerimientos
      if (this.selected !== 'Todos') {
        return requerimientos.filter(requerimiento =>
          requerimiento.estadoDisplay === this.selected
        )
      }
      return requerimientos
    },
    expire () {
      return this.criterio.expire
    },
    isPerson () {
      return this.criterio.isPerson
    },
    showDefinicionesSAF () {
      return this.$store.getters.isSafRole && (this.expire || this.isPerson)
    },
    allAllowedAdmin () {
      return (
        this.criterio &&
      this.criterio.requerimientos &&
      this.criterio.requerimientos.every(r => r.allowed === 'admin')
      )
    },
    buttonIsVisible () {
      return this.criterio.requerimientos.every(r => r.estadoDisplay === 'Revisado ok' || r.estadoDisplay === 'A Revisar')
    },
    hasIncompleteRequerimientos () {
      return (
        this.criterio &&
        this.criterio.requerimientos &&
        this.criterio.requerimientos.some(r => r.estadoDisplay === 'Revisado incompleto')
      )
    }
  },

  methods: {
    updateAfterRequest (updatedRequerimiento) {
      this.$emit('updateRequerimiento', updatedRequerimiento)
    },
    updateCriterionExpiration (data) {
      this.$emit('updateCriterionExpiration', data)
    },
    isAdminRole (allowed) {
      return allowed === 'admin'
    },
    isExpanded () {
      return !Object.values(this.$refs.table.expanded).every((x) => !x)
    },
    getExpandedId () {
      return Object.entries(this.$refs.table.expanded).filter((x) => x[1])[0][0]
    },
    openRequerimiento (props) {
      let isExpanded = this.isExpanded()
      if (isExpanded) {
        let expandedId = this.getExpandedId()
        if (this.$refs[expandedId].isSaving()) {
          this.savingAlert = true
        } else if (this.$refs[expandedId].isModified) {
          this.bindingFunction = () => { props.expanded = !props.expanded }
          this.$refs.confirm.openDialog()
        } else {
          this.currentStatus = props.item.estadoDisplay
          props.expanded = !props.expanded
        }
      } else {
        this.currentStatus = props.item.estadoDisplay
        props.expanded = !props.expanded
      }
    },
    changeCurrentStatus (status) {
      this.currentStatus = status
    },
    setCreateUsers (criterion) {
      let payload = {
        createsUsers: criterion.createsUsers
      }
      this.axios
        .put(`criterio/${criterion.pk}`, payload)
        .then((response) => (criterion.createsUsers = response.data.createsUsers))
        .catch(error => { this.$store.commit('setErrorMsg', error) })
    },
    changeRequerimientoStatus () {
      const requerimientos = this.criterio.requerimientos
      // let variableEstado = !this.flag ? 'Revisado ok' : 'A Revisar'
      const variableEstado = this.criterio.requerimientos.every(r => r.estadoDisplay === 'Revisado ok') ? 'A Revisar' : this.criterio.requerimientos.every(r => r.estadoDisplay === 'A Revisar') ? 'Revisado ok' : 'Revisado ok'
      let payload = {
        estado: variableEstado
      }
      requerimientos.forEach(requerimiento => {
        // if (requerimiento.estadoDisplay === !this.flag ? 'Revisado ok' : 'A Revisar') return
        this.updateAfterRequest(requerimiento)
        this.axios
          .put(`requerimiento/${requerimiento.pk}`, payload)
          .then((response) => {
            requerimiento.estadoDisplay = variableEstado
            this.updateAfterRequest(response)
          })
          .catch(error => {
            this.$store.commit('setErrorMsg', error)
          })
      })
    },
    async handleReiterarObservaciones () {
      const requerimientosIncompletos = this.criterio.requerimientos.filter(
        (req) => req.estadoDisplay === 'Revisado incompleto'
      )

      if (requerimientosIncompletos.length === 0) {
        this.$store.commit('setCustomErrorMsg', 'No hay requerimientos en estado "Revisado incompleto" para reiterar')
        return
      }

      this.loadingReiterar = true
      let hasErrors = false
      let requerimientosOmitidos = []

      const userDeptos = this.$store.state.authUser.departamentos || []
      const defaultDepartamento = userDeptos.length === 1 ? userDeptos[0].id : null

      for (const req of requerimientosIncompletos) {
        let nuevaObservacionText = ''
        let deptoOriginal = null

        if (req.observaciones && req.observaciones.length > 0) {
          const ultimaObservacion = [...req.observaciones]
            .reverse()
            .find(
              (obs) => obs.author && (obs.author.club === 'None' || obs.author.club === null || obs.author.club === '')
            )

          if (ultimaObservacion) {
            if (ultimaObservacion.canEdit !== false) {
              requerimientosOmitidos.push(req.nombre)
              continue
            }
            let textoObservacion = ultimaObservacion.observacion || ''
            const fechaObservacion = ultimaObservacion.modified ? ultimaObservacion.modified.split(' ')[0] : 'fecha desconocida'

            if (textoObservacion.startsWith('Se reitera lo observado el día')) {
              const indiceDosPuntos = textoObservacion.indexOf(':')
              if (indiceDosPuntos !== -1) {
                textoObservacion = textoObservacion.substring(indiceDosPuntos + 1).trim()

                if (textoObservacion.startsWith('"')) {
                  textoObservacion = textoObservacion.substring(1)
                }

                if (textoObservacion.endsWith('"')) {
                  textoObservacion = textoObservacion.slice(0, -1)
                } else if (textoObservacion.endsWith('".')) {
                  textoObservacion = textoObservacion.slice(0, -2) + '.'
                }
              }
            }

            nuevaObservacionText = `Se reitera lo observado el día ${fechaObservacion} : "${textoObservacion}"`
            deptoOriginal = ultimaObservacion.departamento
          } else {
            requerimientosOmitidos.push(req.nombre)
            continue
          }
        } else {
          requerimientosOmitidos.push(req.nombre)
          continue
        }

        const payload = {
          onlyUpdateObservacion: false,
          observacion: nuevaObservacionText,
          estado: 'Revisado incompleto'
        }

        if (deptoOriginal) {
          payload.observacionDepartamento = deptoOriginal
        } else if (defaultDepartamento) {
          payload.observacionDepartamento = defaultDepartamento
        }

        try {
          const response = await this.axios.put(`requerimiento/${req.pk}`, payload)
          if (response.status === 200) {
            Object.assign(req, response.data)
            this.updateAfterRequest(response.data)
          }
        } catch (error) {
          hasErrors = true
        }
      }

      this.loadingReiterar = false

      if (requerimientosOmitidos.length === requerimientosIncompletos.length) {
        this.$store.commit('setCustomErrorMsg', `No se pudo reiterar ninguno por falta de observaciones previas o por tener observaciones editables: ${requerimientosOmitidos.join(', ')}`)
        return
      }

      if (hasErrors) {
        this.$store.commit('setCustomErrorMsg', 'Ocurrieron errores al reiterar algunas observaciones')
      } else {
        if (requerimientosOmitidos.length > 0) {
          this.$store.state.globalInfo = `Se omitieron algunos por falta de observaciones previas o por tener observaciones editables: ${requerimientosOmitidos.join(', ')}`
        } else {
          this.$store.state.globalInfo = 'Todas las observaciones se reiteraron correctamente'
        }
      }
    }
  },

  mounted () {
    this.estados = this.estadosRequerimientos.slice(0)
    this.estados.push(this.selected)
  },

  watch: {
    'criterio.pk' () {
      if (this.$refs.hasOwnProperty('table')) {
        this.$refs.table.expanded = {}
      }
      this.flag = false
    },
    'markCorrectAll' () {
      this.changeRequerimientoStatus()
    }
  }
}
</script>

<style scoped>
  .criterio-header {
    margin: 1em 0 1em 0;
    padding: 0 0 0 0;
  }
  .criterio-subheader {
    background-color: #f1f1f1;
    margin: 0 0 1em 0;
    padding: 0 1.5em 0 1.5em;
  }
  .search-bar {
    margin: 1em 0 1em 0;
    padding: 0 0 0 0;
    width: 20em;
  }
  .select-label{
    width: 18.5em;
  }
  .select-element {
    width: 8em;
  }
  .table-actions {
    padding: 0 0 0 0;
  }
  .custom-font-light {
    font-family: SourceSansProLight, sans-serif;
  }
  .custom-font-regular {
    font-family: SourceSansProRegular, sans-serif;
  }
  .custom-font-semi-bold {
    font-family: SourceSansProSemiBold, sans-serif;
  }
  .custom-blue-text {
    color: #293968;
  }
  .custom-title {
    font-size: 1.6em;
  }
  .custom-text {
    font-size: 1.2em;
  }
  .text-grey {
    color: #413d3d;
  }
  .requerimiento-status {
    margin-left: 0.2em;
  }
  .selected-row {
    background-color: #dedfe4;
  }
  .space-left {
    padding-left: 0.5em;
  }
  .mouse-pointer {
    cursor: pointer;
  }
</style>
